import { track } from '@mobi/account/Utils/analytics'
import { DepositLimitFrequency } from '@mobi/api-types'

export type SignUpAnalyticsKey = 'sign_up_process'

export type SignUpProcessEvent = {
  interaction_type:
    | 'Need Assistance'
    | 'Join'
    | 'Create Password'
    | 'Set Deposit Limit'
    | 'Deposit Limits Skipped'
    | 'No Limit Set Confirmed'
    | 'Deposit Click'
    | 'Already Have Account'
    | 'Go Back to Home Page'
    | 'Start'
    | 'Clicked'
    | 'Error'
    | 'Fail'
    | 'Complete'
    | 'Login Failed'
    | 'Contact Details Verified'
    | 'Account Protection'
    | 'Account Protection Skipped'
  addressMethod: 'Auto' | 'Manual' | undefined
  depositFrequency: DepositLimitFrequency | 'None' | undefined
  limitAmount: string | undefined
  location: string | undefined
  marketingConsent: boolean | undefined
  message: string | undefined
  offerStatus: boolean | undefined
  signUpStarted: boolean | undefined
  accountNumber: number | undefined
  bioState: 'Enabled' | 'Disabled' | undefined
  mfaState: 'Enabled' | 'Disabled' | undefined
}

const defaultSignUpProcessValues: Omit<SignUpProcessEvent, 'interaction_type'> = {
  addressMethod: undefined,
  depositFrequency: undefined,
  limitAmount: undefined,
  location: undefined,
  marketingConsent: undefined,
  message: undefined,
  offerStatus: undefined,
  signUpStarted: undefined,
  accountNumber: undefined,
  bioState: undefined,
  mfaState: undefined,
}

export const signUpProcessTracker = (event: SignUpProcessEvent) => track('sign_up_process', event)

export const trackSignUpNeedAssistance = () =>
  signUpProcessTracker({
    interaction_type: 'Need Assistance',
    ...defaultSignUpProcessValues,
  })

export const trackSignUpJoin = (
  addressMethod: 'Auto' | 'Manual' | undefined,
  marketingConsent: boolean
) =>
  signUpProcessTracker({
    interaction_type: 'Join',
    ...defaultSignUpProcessValues,
    addressMethod: addressMethod,
    marketingConsent,
  })

export const trackSignUpCreatePassword = () =>
  signUpProcessTracker({
    interaction_type: 'Create Password',
    ...defaultSignUpProcessValues,
  })

export const trackSignUpSetDepositLimits = (
  depositFrequency: DepositLimitFrequency,
  limitAmount: string
) =>
  signUpProcessTracker({
    interaction_type: 'Set Deposit Limit',
    ...defaultSignUpProcessValues,
    depositFrequency: depositFrequency ?? 'None',
    limitAmount,
  })

export const trackSignUpNoLimitSetConfirmed = () =>
  signUpProcessTracker({
    interaction_type: 'No Limit Set Confirmed',
    ...defaultSignUpProcessValues,
  })

export const trackSignUpDepositClick = (offerStatus: boolean) =>
  signUpProcessTracker({
    interaction_type: 'Deposit Click',
    ...defaultSignUpProcessValues,
    offerStatus,
  })

export const trackSignUpAlreadyHaveAccount = (signUpStarted: boolean) =>
  signUpProcessTracker({
    interaction_type: 'Already Have Account',
    ...defaultSignUpProcessValues,
    signUpStarted,
  })

export const trackSignUpGoBackHomePage = (location: string, signUpStarted: boolean) =>
  signUpProcessTracker({
    interaction_type: 'Go Back to Home Page',
    ...defaultSignUpProcessValues,
    location,
    signUpStarted,
  })

export const trackSignUpStart = () =>
  signUpProcessTracker({
    interaction_type: 'Start',
    ...defaultSignUpProcessValues,
  })

export const trackSignUpClick = (
  location: 'Header' | 'Login Modal' | 'Hamburger' // TODO: Update calls
) =>
  signUpProcessTracker({
    interaction_type: 'Clicked',
    ...defaultSignUpProcessValues,
    location,
  })

export const trackSignUpError = (location: string, message: string) =>
  signUpProcessTracker({
    interaction_type: 'Error',
    ...defaultSignUpProcessValues,
    location,
    message,
  })

export const trackSignUpVerificationFail = (message: string) =>
  signUpProcessTracker({
    interaction_type: 'Fail',
    ...defaultSignUpProcessValues,
    message,
  })

export const trackSignUpComplete = () =>
  signUpProcessTracker({
    interaction_type: 'Complete',
    ...defaultSignUpProcessValues,
  })

export const trackSignUpLoginFailed = (accountNumber: number) =>
  signUpProcessTracker({
    interaction_type: 'Login Failed',
    ...defaultSignUpProcessValues,
    accountNumber,
  })

export const trackSignUpVerifyContactDetails = () =>
  signUpProcessTracker({
    interaction_type: 'Contact Details Verified',
    ...defaultSignUpProcessValues,
  })

export const trackSignUpAccountProtection = (
  mfaState: 'Enabled' | 'Disabled' | undefined,
  bioState: 'Enabled' | 'Disabled' | undefined
) => {
  signUpProcessTracker({
    interaction_type: 'Account Protection',
    ...defaultSignUpProcessValues,
    mfaState,
    bioState,
  })
}

export const trackSignUpAccountProtectionSkipped = () => {
  signUpProcessTracker({
    interaction_type: 'Account Protection Skipped',
    ...defaultSignUpProcessValues,
  })
}
